import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2 } from "lucide-react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { items, subtotal, removeItem, clear } = useCart();
  const navigate = useNavigate();

  const [settings, setSettings] = useState(null);
  const [slots, setSlots] = useState([]);
  const [pickupType, setPickupType] = useState("asap");
  const [pickupTime, setPickupTime] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/pickup-slots").then((r) => setSlots(r.data.slots)).catch(() => {});
  }, []);

  const taxRate = settings?.tax_rate ?? 0.065;
  const tax = useMemo(() => subtotal * taxRate, [subtotal, taxRate]);
  const total = subtotal + tax;
  const paused = settings?.ordering_paused;

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-stone-900">Your cart is empty</h1>
        <button onClick={() => navigate("/menu")} className="mt-6 rounded-full bg-orange-700 px-6 py-3 font-bold text-white hover:bg-orange-800">
          Browse the menu
        </button>
      </div>
    );
  }

  const handlePay = async () => {
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your contact details");
      return;
    }
    if (pickupType === "scheduled" && !pickupTime) {
      toast.error("Please select a pickup time");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          item_id: i.item_id,
          quantity: i.quantity,
          selected_options: (i.selected_options || []).map((o) => ({ group: o.group, label: o.label })),
          special_instructions: i.special_instructions || null,
        })),
        customer: form,
        pickup_type: pickupType,
        pickup_time: pickupType === "scheduled" ? pickupTime : null,
        special_instructions: notes.trim() || null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "items_unavailable") {
        toast.error("Some items just sold out. Please remove them and try again.");
      } else if (detail?.code === "ordering_paused") {
        toast.error(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Checkout failed");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <h1 className="font-display text-4xl font-extrabold text-stone-900">Checkout</h1>
      <p className="mt-2 text-stone-600">Pickup only · Orlando, FL</p>

      {paused && (
        <div className="mt-6 rounded-2xl border border-amber-400 bg-amber-100 px-5 py-4 text-amber-900">
          Online ordering is currently paused. Checkout is temporarily unavailable.
        </div>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-5">
        {/* Left: forms */}
        <div className="space-y-8 lg:col-span-3">
          {/* Pickup */}
          <div className="rounded-2xl border border-stone-200 bg-white p-6">
            <h2 className="font-display text-xl font-bold text-stone-900">Pickup Time</h2>
            <RadioGroup value={pickupType} onValueChange={setPickupType} className="mt-4 grid grid-cols-2 gap-3">
              <label className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 ${pickupType === "asap" ? "border-orange-600 bg-orange-50" : "border-stone-200"}`}>
                <RadioGroupItem value="asap" id="asap" data-testid="pickup-type-asap-radio" />
                <div><div className="font-semibold text-stone-900">ASAP</div><div className="text-xs text-stone-500">~30 min</div></div>
              </label>
              <label className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 ${pickupType === "scheduled" ? "border-orange-600 bg-orange-50" : "border-stone-200"}`}>
                <RadioGroupItem value="scheduled" id="scheduled" data-testid="pickup-type-scheduled-radio" />
                <div><div className="font-semibold text-stone-900">Schedule</div><div className="text-xs text-stone-500">Pick a time</div></div>
              </label>
            </RadioGroup>

            {pickupType === "scheduled" && (
              <div className="mt-4">
                <Label className="text-sm font-semibold">Pickup slot</Label>
                <Select value={pickupTime} onValueChange={setPickupTime}>
                  <SelectTrigger className="mt-2" data-testid="pickup-time-select">
                    <SelectValue placeholder="Select a pickup time" />
                  </SelectTrigger>
                  <SelectContent>
                    {slots.length === 0 && <SelectItem value="none" disabled>No slots available</SelectItem>}
                    {slots.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Contact */}
          <div className="rounded-2xl border border-stone-200 bg-white p-6">
            <h2 className="font-display text-xl font-bold text-stone-900">Contact Details</h2>
            <div className="mt-4 grid gap-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="checkout-guest-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Ada Obi" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" data-testid="checkout-guest-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" placeholder="(407) 555-0100" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" data-testid="checkout-guest-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="you@email.com" />
                </div>
              </div>
              <div>
                <Label htmlFor="notes">Order notes (optional)</Label>
                <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 resize-none" rows={2} placeholder="Anything the kitchen should know" />
              </div>
            </div>
          </div>
        </div>

        {/* Right: summary */}
        <div className="lg:col-span-2">
          <div className="sticky top-24 rounded-2xl border border-stone-200 bg-white p-6">
            <h2 className="font-display text-xl font-bold text-stone-900">Order Summary</h2>
            <div className="mt-4 space-y-3">
              {items.map((it) => (
                <div key={it.cartId} className="flex items-start justify-between gap-2 border-b border-stone-100 pb-3">
                  <div>
                    <div className="font-semibold text-stone-900">{it.quantity}× {it.name}</div>
                    {it.selected_options?.length > 0 && (
                      <div className="text-xs text-stone-500">{it.selected_options.map((o) => o.label).join(", ")}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-stone-900">{money(it.unit_price * it.quantity)}</span>
                    <button onClick={() => removeItem(it.cartId)} className="text-stone-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-stone-600">Subtotal</span><span className="font-semibold">{money(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-stone-600">Tax ({(taxRate * 100).toFixed(2)}%)</span><span className="font-semibold">{money(tax)}</span></div>
              <div className="flex justify-between border-t border-stone-200 pt-2 text-lg font-bold text-stone-900"><span>Total</span><span>{money(total)}</span></div>
            </div>
            <button
              data-testid="stripe-payment-submit-button"
              onClick={handlePay}
              disabled={submitting || paused}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-orange-700 py-3.5 font-bold text-white transition-colors duration-200 hover:bg-orange-800 disabled:cursor-not-allowed disabled:bg-stone-300"
            >
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : `Pay ${money(total)}`}
            </button>
            <p className="mt-3 text-center text-xs text-stone-400">Secure payment via Stripe · Card tested with 4242 4242 4242 4242</p>
          </div>
        </div>
      </div>
    </div>
  );
}
